import React, { useState, useEffect, useRef } from 'react';
import { DesktopService } from '../../services/desktopService';
import useVibeStore from '../../store/useVibeStore';
import useChatStore from '../../store/useChatStore';

import { NodeService } from '../../services/NodeService';
import { auth } from '../../lib/firebase';

const AntigravityControl = ({ targetUid, targetDeviceId, onCommandSent, nodeId, mode = 'antigravity' }) => {
    const [prompt, setPrompt] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [status, setStatus] = useState('Idle');

    // Global Store State
    const { pendingAction, clearPendingAction } = useVibeStore();
    const { activeChat, isAiThinking, setActiveChat, subscribeToMessages } = useChatStore();
    const isAgentProcessing = activeChat?.isAgentProcessing === true;
    const agentStatus = activeChat?.agentStatus || 'Idle';

    const textareaRef = useRef(null);

    const initRef = useRef(false);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'inherit';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 180)}px`;
        }
    }, [prompt]);

    // Initial Scan on Mount
    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;

        const initializeAndScan = async () => {
            if (!targetUid || !targetDeviceId) return;
            
            try {
                let chatId = activeChat?.id;
                
                if (!chatId && nodeId) {
                    const existingSessions = await NodeService.getNodeSessions(nodeId);
                    const agSession = existingSessions.find(s => s.title === "Antigravity Session");
                    
                    if (agSession) {
                        chatId = agSession.id;
                    } else {
                        const initialSessionSettings = {};
                        chatId = await NodeService.createSession(auth.currentUser.uid, nodeId, "Antigravity Session", initialSessionSettings);
                    }
                    
                    await setActiveChat(chatId);
                    subscribeToMessages(chatId);
                }

                if (chatId) {
                    await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                        command: 'scan_ui_state',
                        target_ide: mode
                    }, chatId);
                }
            } catch (err) {
                console.error("Failed to initialize or scan:", err);
            }
        };

        initializeAndScan();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetUid, targetDeviceId, nodeId]);

    const handleSendPrompt = async () => {
        if (!prompt.trim() || isSending) return;
        setIsSending(true);
        setStatus(`Sending to ${mode === 'vscode' ? 'VS Code' : 'Antigravity'}...`);

        try {
            let chatId = activeChat?.id;
            
            if (!chatId && nodeId) {
                const initialSessionSettings = {};
                chatId = await NodeService.createSession(auth.currentUser.uid, nodeId, "Antigravity Session", initialSessionSettings);
                await setActiveChat(chatId);
                subscribeToMessages(chatId);
            }

            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'run_antigravity_control',
                action: 'type',
                text: prompt.trim(),
                target_ide: mode
            }, chatId);
            setPrompt('');
            setStatus('Prompt sent! Adjusting engine...');
            clearPendingAction(); // User overrode the plan with a custom prompt
            if (onCommandSent) onCommandSent();
        } catch (error) {
            console.error("Failed to send prompt:", error);
            setStatus('Error sending prompt.');
        } finally {
            setIsSending(false);
            setTimeout(() => setStatus('Idle'), 3000);
        }
    };

    const handleProceed = async () => {
        setIsSending(true);
        setStatus('Clicking Proceed...');
        try {
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'vibe_accept', // Vibe accept triggers click_accept_changes spatially right now in python
                target_ide: mode
            }, activeChat?.id);
            setStatus('Proceeding...');
            clearPendingAction();
            if (onCommandSent) onCommandSent();
        } catch (error) {
            console.error("Failed to proceed:", error);
            setStatus('Error clicking proceed.');
        } finally {
            setIsSending(false);
            setTimeout(() => setStatus('Idle'), 3000);
        }
    };

    const handleReject = async () => {
        setIsSending(true);
        setStatus('Clicking Reject...');
        try {
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'vibe_reject',
                target_ide: mode
            }, activeChat?.id);
            setStatus('Rejecting...');
            clearPendingAction();
            if (onCommandSent) onCommandSent();
        } catch (error) {
            console.error("Failed to reject:", error);
            setStatus('Error clicking reject.');
        } finally {
            setIsSending(false);
            setTimeout(() => setStatus('Idle'), 3000);
        }
    };

    const handleTerminate = async () => {
        setIsSending(true);
        setStatus('Clicking Stop/Terminate...');
        try {
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'vibe_terminate',
                target_ide: mode
            }, activeChat?.id);
            setStatus('Terminated.');
            clearPendingAction();
            if (onCommandSent) onCommandSent();
        } catch (error) {
            console.error("Failed to terminate:", error);
            setStatus('Error clicking terminate.');
        } finally {
            setIsSending(false);
            setTimeout(() => setStatus('Idle'), 3000);
        }
    };

    const handleChangeAgent = async (e) => {
        const selectedAgent = e.target.value;
        if (!selectedAgent) return;
        
        setIsSending(true);
        setStatus(`Switching agent to ${selectedAgent}...`);
        try {
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'change_agent_type',
                target_ide: mode,
                agent_type: selectedAgent
            }, activeChat?.id);
            setStatus(`Agent switched to ${selectedAgent}.`);
        } catch (error) {
            console.error("Failed to change agent:", error);
            setStatus('Error switching agent.');
        } finally {
            setIsSending(false);
            setTimeout(() => setStatus('Idle'), 3000);
        }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${status === 'Idle' && agentStatus === 'Idle' ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} />
                        <span className="text-xs font-bold uppercase tracking-widest text-[var(--text-muted)]">
                            {status !== 'Idle' ? status : agentStatus}
                        </span>
                    </div>
                    
                    {mode === 'vscode' && (
                        <select 
                            onChange={handleChangeAgent}
                            disabled={isSending || isAgentProcessing}
                            className="bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-xs rounded-lg px-2 py-1 focus:outline-none focus:border-[var(--accent-primary)] transition-colors"
                            defaultValue=""
                        >
                            <option value="" disabled>Switch Agent...</option>
                            <option value="architect">Architect</option>
                            <option value="code">Code</option>
                            <option value="ask">Ask</option>
                            <option value="debug">Debug</option>
                            <option value="orchestrator">Orchestrator</option>
                        </select>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    {(mode === 'vscode' || ['waiting_for_proceed', 'waiting_for_run_terminal', 'waiting_for_accept_terminal', 'waiting_for_accept_changes'].includes(pendingAction)) && (
                        <button
                            onClick={handleProceed}
                            disabled={isSending}
                            className="px-4 py-2 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-[var(--bg-main)] text-sm font-bold rounded-xl transition-all disabled:opacity-50"
                        >
                            {mode === 'vscode' ? 'Accept' : (
                                pendingAction === 'waiting_for_proceed' ? 'Proceed' :
                                pendingAction === 'waiting_for_run_terminal' ? 'Run' :
                                pendingAction === 'waiting_for_accept_terminal' ? 'Accept Terminal' :
                                'Accept all'
                            )}
                        </button>
                    )}

                    {(mode === 'vscode' || ['waiting_for_run_terminal', 'waiting_for_accept_terminal'].includes(pendingAction)) && (
                        <button
                            onClick={handleReject}
                            disabled={isSending}
                            className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-bold rounded-xl transition-all disabled:opacity-50"
                        >
                            Reject
                        </button>
                    )}

                    {(mode === 'vscode' || isAiThinking || pendingAction || isSending || isAgentProcessing) && (
                        <button
                            onClick={handleTerminate}
                            disabled={isSending}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20 text-sm font-bold rounded-xl transition-all disabled:opacity-50 flex items-center gap-1"
                            title="Force Stop Agent"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" /></svg>
                            Stop
                        </button>
                    )}
                </div>
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSendPrompt();
                }}
                className="relative flex flex-col bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] rounded-[24px] shadow-sm transition-all focus-within:border-[var(--text-muted)]"
            >
                <textarea
                    ref={textareaRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendPrompt();
                        }
                    }}
                    placeholder={`Message ${mode === 'vscode' ? 'Roo Code' : 'Antigravity Engine'}... (Shift+Enter for new line)`}
                    rows={1}
                    className="w-full bg-transparent text-[var(--text-main)] placeholder-[var(--text-muted)]/60 text-[15px] focus:outline-none resize-none px-6 py-4 min-h-[40px] custom-scrollbar overflow-y-auto font-medium rounded-t-[24px]"
                    style={{ maxHeight: '180px' }}
                />

                <div className="flex items-center justify-end px-5 pb-3 pt-0.5">
                    <button
                        type="submit"
                        disabled={!prompt.trim() || isSending}
                        className="shrink-0 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] active:scale-95 disabled:opacity-50 text-[var(--bg-main)] p-2.5 rounded-full transition-all flex items-center justify-center focus:outline-none shadow-md"
                    >
                        {isSending ? (
                            <svg className="animate-spin w-4 h-4 text-inherit" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <svg className="w-4 h-4 rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
                            </svg>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AntigravityControl;
